import { CLINICAL_SPECIALISM_LABEL, MARKET_NAMES } from '@hc/shared';
import type { HomesQuery } from '../api/client.js';

// Filter model + value labels for the Homes screener (care-home grain, GET
// /homes). Same shape as Triage's screener-filters.ts — categorical Sets,
// numeric ranges, a free-text query — keyed on the /homes wire params.
//
// RULES carried from the spec (TERMINAL_HOME_GRAIN_SPEC_2026-10-15):
//   * Lifecycle defaults to Operating and is always sent explicitly, so the day
//     homes in development land no total in this panel moves.
//   * One "Who it cares for" filter on cohorts (the 9 agreed tokens); clinical
//     specialisms are shown in Home record only.
//   * Location precision is Precise / Locality only — raw geo_precision labels
//     never reach the client.

export const HOME_CATEGORICAL_KEYS = [
  'lifecycle',
  'country',
  'urbanicity',
  'setting',
  'careLevel',
  'cohort',
  'stay',
  'pinLevel',
  'operatorSector',
  'tenure',
  'landlordType',
  'quality',
  'altSignal',
  'ratingFamily',
  'publicFunding',
  'payerPressure',
  'floodVerdict',
  'heatVerdict',
] as const;
export type HomeCategoricalKey = (typeof HOME_CATEGORICAL_KEYS)[number];
export type HomeCategoricals = Readonly<Record<HomeCategoricalKey, ReadonlySet<string>>>;

export const HOME_CATEGORICAL_LABELS: Record<HomeCategoricalKey, string> = {
  lifecycle: 'Lifecycle',
  country: 'Country',
  urbanicity: 'Urban / rural',
  setting: 'Setting',
  careLevel: 'Care level',
  cohort: 'Specialisation',
  stay: 'Stay',
  pinLevel: 'Pin',
  operatorSector: 'Ownership type',
  tenure: 'Freehold tenure',
  landlordType: 'Landlord type',
  quality: 'Quality (grade, else signal)',
  altSignal: 'Other-source signal',
  ratingFamily: 'Regulator',
  publicFunding: 'Public funding',
  payerPressure: 'Payer pressure',
  floodVerdict: 'Flood exposure',
  heatVerdict: 'Heat exposure',
};

export const DEFAULT_LIFECYCLE = 'operating';

export function defaultHomeCategoricals(): HomeCategoricals {
  const out = Object.fromEntries(
    HOME_CATEGORICAL_KEYS.map((k) => [k, new Set<string>()]),
  ) as unknown as Record<HomeCategoricalKey, ReadonlySet<string>>;
  out.lifecycle = new Set([DEFAULT_LIFECYCLE]);
  return out;
}

// --- ranges -------------------------------------------------------------

export const HOME_RANGE_KEYS = [
  'beds',
  'buildingYear',
  'fee',
  'incomePctl',
  'growth65',
  'growth80',
  'placesPer1000Elderly',
  'vacancyRate',
  'operatorHomes',
] as const;
export type HomeRangeKey = (typeof HOME_RANGE_KEYS)[number];

/** [min, max]; null = open on that side. */
export type HomeRange = readonly [number | null, number | null];
export type HomeRanges = Readonly<Record<HomeRangeKey, HomeRange>>;

export interface HomeRangeSpec {
  key: HomeRangeKey;
  label: string;
  /** Slider bounds — a thumb resting on a bound means "open". */
  min: number;
  max: number;
  step: number;
  /** Display formatter for a bound. */
  format: (v: number) => string;
  hint?: string;
}

const int = (v: number) => Math.round(v).toLocaleString('en-GB');
const pct = (v: number) => `${v}%`;

export const HOME_RANGE_SPECS: readonly HomeRangeSpec[] = [
  { key: 'beds', label: 'Beds (published)', min: 0, max: 300, step: 5, format: int },
  {
    key: 'buildingYear',
    label: 'Building year',
    min: 1850,
    max: 2026,
    step: 1,
    format: (v) => String(v),
  },
  {
    key: 'fee',
    label: 'Fee',
    min: 0,
    max: 500,
    step: 5,
    format: int,
    hint: 'In the home’s own published unit (per day / week / month) — narrow by country first.',
  },
  {
    key: 'incomePctl',
    label: 'Catchment income percentile',
    min: 0,
    max: 100,
    step: 1,
    format: int,
  },
  // growth_65_pct / growth_80_pct are fractions (0.18 = +18%); the slider runs
  // in percentage points and the wire value is divided back by 100.
  { key: 'growth65', label: 'Growth 65+ (projected)', min: -20, max: 60, step: 1, format: pct },
  { key: 'growth80', label: 'Growth 80+ (projected)', min: -20, max: 100, step: 1, format: pct },
  {
    key: 'placesPer1000Elderly',
    label: 'Places per 1,000 elderly',
    min: 0,
    max: 150,
    step: 1,
    format: int,
  },
  { key: 'vacancyRate', label: 'Care vacancy rate', min: 0, max: 20, step: 0.5, format: pct },
  { key: 'operatorHomes', label: 'Operator homes', min: 1, max: 200, step: 1, format: int },
];

export const HOME_RANGE_SPEC_BY_KEY = Object.fromEntries(
  HOME_RANGE_SPECS.map((s) => [s.key, s]),
) as Readonly<Record<HomeRangeKey, HomeRangeSpec>>;

/** Ranges whose UI unit differs from the wire unit: wire = ui × factor. */
const WIRE_FACTOR: Partial<Record<HomeRangeKey, number>> = { growth65: 0.01, growth80: 0.01 };

export function defaultHomeRanges(): HomeRanges {
  return Object.fromEntries(HOME_RANGE_KEYS.map((k) => [k, [null, null]])) as unknown as HomeRanges;
}

export function isHomeRangeActive(r: HomeRange): boolean {
  return r[0] !== null || r[1] !== null;
}

export function summarizeHomeRange(spec: HomeRangeSpec, r: HomeRange): string {
  if (!isHomeRangeActive(r)) return 'Any';
  if (r[0] !== null && r[1] !== null) return `${spec.format(r[0])} – ${spec.format(r[1])}`;
  if (r[0] !== null) return `≥ ${spec.format(r[0])}`;
  return `≤ ${spec.format(r[1]!)}`;
}

// --- has-data toggles ---------------------------------------------------

/** "Has data" filters: the column must be non-null. Most attributes are
 *  partial, so these are how a user asks for "only homes with published beds". */
export const HOME_HAS_OPTIONS: ReadonlyArray<{ column: string; label: string }> = [
  { column: 'beds', label: 'Published beds' },
  { column: 'operator_entity_id', label: 'Linked operator company' },
  { column: 'rating', label: 'Regulator rating' },
  { column: 'landlord_key', label: 'Named landlord' },
  { column: 'fee_value', label: 'Published fee' },
  { column: 'public_funding', label: 'Public-funding status' },
  { column: 'building_year_built', label: 'Building year' },
  { column: 'occupancy_value', label: 'Published occupancy' },
  { column: 'lat', label: 'Map pin' },
];

// --- counting + wire query ---------------------------------------------

export function countActiveHomeFilters(
  c: HomeCategoricals,
  r: HomeRanges,
  has: ReadonlySet<string>,
  q: string,
): number {
  let n = 0;
  for (const k of HOME_CATEGORICAL_KEYS) {
    const s = c[k];
    if (k === 'lifecycle') {
      // The Operating default is not a user filter.
      if (!(s.size === 1 && s.has(DEFAULT_LIFECYCLE))) n++;
    } else if (s.size > 0) n++;
  }
  for (const k of HOME_RANGE_KEYS) if (isHomeRangeActive(r[k])) n++;
  n += has.size;
  if (q.trim().length >= 2) n++;
  return n;
}

const fmtWire = (v: number | null, factor: number) =>
  v === null ? '' : String(Math.round(v * factor * 10000) / 10000);

/** The filter part of a /homes or /homes/facets query. */
export function buildHomesFilterQuery(
  c: HomeCategoricals,
  r: HomeRanges,
  has: ReadonlySet<string>,
  q: string,
): HomesQuery {
  const out: HomesQuery = {};
  for (const k of HOME_CATEGORICAL_KEYS) {
    const s = c[k];
    if (s.size) out[k] = Array.from(s).sort().join(',');
  }
  // An emptied Lifecycle set means "all lifecycles" — say so explicitly, because
  // an absent param means operating-only on the server.
  if (c.lifecycle.size === 0) out['lifecycle'] = 'operating,development';
  for (const k of HOME_RANGE_KEYS) {
    const v = r[k];
    if (!isHomeRangeActive(v)) continue;
    const f = WIRE_FACTOR[k] ?? 1;
    out[k] = `${fmtWire(v[0], f)},${fmtWire(v[1], f)}`;
  }
  if (has.size) out['has'] = Array.from(has).sort().join(',');
  const term = q.trim();
  if (term.length >= 2) out['q'] = term.slice(0, 100);
  return out;
}

// --- value labels -------------------------------------------------------

/** "requires_improvement" → "Requires improvement". Verbatim text with spaces
 *  is returned as-is (ratings are the register's own words). */
export function humanizeToken(v: string): string {
  if (!/^[a-z0-9_]+$/.test(v)) return v;
  const s = v.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const VALUE_LABELS: Partial<
  Record<HomeCategoricalKey | 'fineType' | 'ratingGrade', Record<string, string>>
> = {
  lifecycle: { operating: 'Operating', development: 'In development' },
  setting: { residential: 'Residential', own_home_scheme: 'Own-home scheme' },
  careLevel: { nursing: 'Nursing', personal_care: 'Personal care', support_only: 'Support only' },
  stay: { permanent: 'Permanent', short_stay: 'Short stay' },
  pinLevel: { precise: 'Precise', locality: 'Locality only' },
  urbanicity: { cities: 'City', towns_and_suburbs: 'Town & suburbs', rural_areas: 'Rural' },
  operatorSector: {
    public: 'Public',
    nonprofit: 'Non-profit',
    non_profit: 'Non-profit',
    private: 'Private',
    unknown: 'Unknown',
  },
  tenure: {
    operator_owns: 'Operator freehold',
    landlord: 'Leased from landlord',
    divided: 'Divided ownership',
    owner_class_only: 'Owner type only',
    unknown: 'Unknown',
  },
  ratingGrade: { good: 'Good', requires_improvement: 'Requires improvement', poor: 'Poor' },
  // Grade where the regulator gives one; otherwise the care regulator's SIGNALS — labelled as signals, never grades.
  quality: {
    good: 'Grade: Good',
    requires_improvement: 'Grade: Requires improvement',
    poor: 'Grade: Poor',
    signal_positive: 'No grade · positive signal',
    signal_mixed: 'No grade · mixed signals',
    signal_neutral: 'No grade · indicators only',
    signal_negative: 'No grade · negative signal',
  },
  altSignal: {
    positive: 'Positive',
    mixed: 'Mixed',
    neutral: 'Neutral only',
    negative: 'Negative',
  },
  landlordType: {
    reit: 'REIT',
    fund: 'Fund',
    propco_investor: 'Propco / investor',
    social_housing: 'Social housing',
    finance_lessor: 'Finance lessor',
  },
  ratingFamily: {
    gb_cqc: 'CQC (England)',
    gb_scot_ci: 'Care Inspectorate (Scotland)',
    gb_ciw: 'CIW (Wales)',
    fr_has: 'HAS (France)',
    es_registry: 'Registry standing (Spain)',
    ie_hiqa: 'HIQA (Ireland)',
    de_nrw_wtg: 'WTG NRW (Germany)',
    de_berlin_wtg: 'WTG Berlin (Germany)',
    de_hh_orders: 'Hamburg orders (Germany)',
    be_zorginspectie: 'Zorginspectie (Flanders)',
    dk_stps: 'STPS (Denmark)',
  },
  publicFunding: { accepted: 'Accepts public funding', not_accepted: 'No public funding' },
  payerPressure: { pressure: 'Pressure', no_pressure: 'No pressure' },
  floodVerdict: { none: 'None', low: 'Low', material: 'Material' },
  heatVerdict: { none: 'None', low: 'Low', material: 'Material' },
  fineType: {
    nursing_home: 'Nursing home',
    residential_care: 'Residential care',
    assisted_living: 'Assisted living',
  },
};

/** Display label for a filter / column value. `null` is the facet's
 *  "not recorded" bucket. */
export function homeValueLabel(
  key: HomeCategoricalKey | 'fineType' | 'ratingGrade',
  v: string | null,
): string {
  if (v === null) return 'Not recorded';
  if (key === 'country') return MARKET_NAMES[v as keyof typeof MARKET_NAMES] ?? v;
  if (key === 'cohort') return cohortLabel(v);
  return VALUE_LABELS[key]?.[v] ?? humanizeToken(v);
}

const COHORT_LABELS: Record<string, string> = {
  dementia: 'Dementia',
  complex_care: 'Complex care',
};

export function cohortLabel(v: string): string {
  return COHORT_LABELS[v] ?? CLINICAL_SPECIALISM_LABEL[v] ?? humanizeToken(v);
}

/** Pin precision for UI: Precise / Locality only — never a raw label. */
export function pinLabel(v: unknown): string | null {
  if (v === 'precise') return 'Precise';
  if (v === 'locality') return 'Locality only';
  return null;
}

/** The three provenance badges a `*_basis` value collapses to. */
export type BasisBadge = 'stated' | 'statutory' | 'modelled';

/** Collapse a `*_basis` value to stated / statutory / modelled. Statutory =
 *  derived from the market's statute (e.g. "no nursing in this setting by
 *  law"); modelled = inferred by Corryk (rules, tests, inheritance, proxies,
 *  estimates); everything else is a published statement (register, licence,
 *  web page, filing). */
export function basisBadge(basis: unknown): BasisBadge | null {
  if (typeof basis !== 'string' || basis.trim() === '') return null;
  const b = basis.toLowerCase();
  if (b.startsWith('statutory')) return 'statutory';
  if (
    /^(modelled|setting_test|operator_inherited|derived|estimated|nuts_proxy|own_home_region|name\b)/.test(
      b,
    )
  )
    return 'modelled';
  return 'stated';
}

// --- provenance ---------------------------------------------------------

/** "beds:counsel_pending" → "beds (counsel review pending)". */
export function termLabel(flag: string): string {
  const [field, status] = flag.split(':');
  const STATUS: Record<string, string> = {
    counsel_pending: 'counsel review pending',
    terms_restricted: 'terms restricted',
    share_alike_flagged: 'share-alike licence',
    licence_unknown: 'licence unknown',
  };
  if (!status) return flag;
  return `${field} (${STATUS[status] ?? humanizeToken(status).toLowerCase()})`;
}
